/**
 * The radio chain (audio-direction §5): band-pass 300–3,400 Hz (two poles each side), light drive, a light
 * bit-crush blended with the clean signal, a static bed that runs while the channel is open, and squelch bursts
 * on open and close. Built once per context; `transmit` schedules one line (live or offline).
 */
import { createRng, nextFloat } from '../../core/rng'
import { fillNoise, tanhCurve } from '../sfx/dsp'
import type { CommsConfig } from './commsSchema'

export interface Transmission {
  /** Squelch open, voice start, voice end, squelch close end (context time). */
  open: number
  voiceStart: number
  voiceEnd: number
  close: number
  source: AudioBufferSourceNode
}

export interface Radio {
  /** Where the voice goes (before the band-pass). */
  readonly input: AudioNode
  /** Schedules a transmission of `voice` with its squelch open at `at`. */
  transmit(voice: AudioBuffer, at: number): Transmission
  dispose(): void
}

/** A stair-step curve quantising to 2^bits levels (the bit-crush). */
export function crushCurve(bits: number, points = 4096): Float32Array<ArrayBuffer> {
  const steps = Math.pow(2, bits) / 2
  const curve = new Float32Array(points)
  for (let i = 0; i < points; i++) {
    const x = (i / (points - 1)) * 2 - 1
    curve[i] = Math.round(x * steps) / steps
  }
  return curve
}

/** Static: soft band-limited hiss plus sparse crackles (seeded, loopable). */
function staticBuffer(ac: BaseAudioContext, cfg: CommsConfig['radio']['static'], seed: number): AudioBuffer {
  const rng = createRng(seed)
  const n = Math.max(1, Math.ceil(cfg.seconds * ac.sampleRate))
  const b = ac.createBuffer(1, n, ac.sampleRate)
  const d = b.getChannelData(0)
  fillNoise(d, rng, 'pink')
  for (let i = 0; i < d.length; i++) d[i] *= 0.5
  for (let k = 0; k < cfg.crackles; k++) {
    const at = Math.floor(nextFloat(rng) * n)
    const amp = 0.6 + nextFloat(rng) * 1.4
    for (let j = 0; j < 24 && at + j < n; j++) d[at + j] += (nextFloat(rng) * 2 - 1) * amp * Math.exp(-j / 5)
  }
  return b
}

export function createRadio(ac: BaseAudioContext, dest: AudioNode, cfg: CommsConfig, seed: number): Radio {
  const r = cfg.radio
  const input = ac.createGain()
  let last: AudioNode = input
  for (const [type, hz] of [
    ['highpass', r.highpassHz],
    ['highpass', r.highpassHz],
    ['lowpass', r.lowpassHz],
    ['lowpass', r.lowpassHz],
  ] as const) {
    const f = ac.createBiquadFilter()
    f.type = type
    f.frequency.value = hz
    f.Q.value = r.q
    last.connect(f)
    last = f
  }
  const drive = ac.createWaveShaper()
  drive.curve = tanhCurve(r.drive)
  drive.oversample = '2x'
  last.connect(drive)
  const clean = ac.createGain()
  clean.gain.value = 1 - r.crushMix
  const crush = ac.createWaveShaper()
  crush.curve = crushCurve(r.bits)
  const crushed = ac.createGain()
  crushed.gain.value = r.crushMix
  drive.connect(clean)
  drive.connect(crush)
  crush.connect(crushed)
  const out = ac.createGain()
  out.gain.value = r.voiceGain
  clean.connect(out)
  crushed.connect(out)
  out.connect(dest)

  const bed = staticBuffer(ac, r.static, seed)
  const bedSrc = ac.createBufferSource()
  bedSrc.buffer = bed
  bedSrc.loop = true
  const bedGain = ac.createGain()
  bedGain.gain.value = 0
  bedSrc.connect(bedGain)
  bedGain.connect(input)
  bedSrc.start(0)
  const rng = createRng(seed ^ 0x5c)

  function squelch(at: number, seconds: number): void {
    const s = ac.createBufferSource()
    s.buffer = bed
    const g = ac.createGain()
    g.gain.setValueAtTime(0, at)
    g.gain.linearRampToValueAtTime(r.squelch.gain, at + 0.004)
    g.gain.setTargetAtTime(0, at + 0.01, seconds / 4)
    s.connect(g)
    g.connect(input)
    s.start(at, nextFloat(rng) * Math.max(0, bed.duration - seconds - 0.01))
    s.stop(at + seconds + 0.05)
  }

  return {
    input,
    transmit(voice: AudioBuffer, at: number): Transmission {
      const voiceStart = at + r.squelch.lead
      const voiceEnd = voiceStart + voice.duration
      const closeAt = voiceEnd + r.squelch.tail
      squelch(at, r.squelch.open)
      bedGain.gain.setTargetAtTime(r.static.gain, at, 0.01)
      const src = ac.createBufferSource()
      src.buffer = voice
      src.connect(input)
      src.start(voiceStart)
      squelch(closeAt, r.squelch.close)
      bedGain.gain.setTargetAtTime(0, closeAt + 0.02, 0.02)
      return { open: at, voiceStart, voiceEnd, close: closeAt + r.squelch.close, source: src }
    },
    dispose(): void {
      bedSrc.stop()
      out.disconnect()
    },
  }
}
